/**
 * Validation utility functions for consistent form validation across the application
 */

// Common validation rules
export const validationRules = {
  // Email validation
  email: {
    required: 'Email is required',
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Please enter a valid email address'
    }
  },

  // Password validation
  password: {
    required: 'Password is required',
    minLength: {
      value: 8,
      message: 'Password must be at least 8 characters'
    },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    }
  },

  // Name validation
  name: {
    required: 'Name is required',
    minLength: {
      value: 2,
      message: 'Name must be at least 2 characters'
    },
    pattern: {
      value: /^[a-zA-Z\s'-]+$/,
      message: 'Name can only contain letters, spaces, hyphens, and apostrophes'
    }
  },

  // Phone validation
  phone: {
    pattern: {
      value: /^[\d\s\-\(\)\+]+$/,
      message: 'Please enter a valid phone number'
    }
  },

  // Required field
  required: (fieldName) => ({
    required: `${fieldName} is required`
  }),

  // Number validation
  number: {
    pattern: {
      value: /^\d*\.?\d+$/,
      message: 'Please enter a valid number'
    }
  },

  // Positive number
  positiveNumber: {
    pattern: {
      value: /^\d*\.?\d+$/,
      message: 'Please enter a valid number'
    },
    validate: (value) => {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        return 'Must be a positive number';
      }
      return true;
    }
  },

  // Currency (positive number with 2 decimals)
  currency: {
    pattern: {
      value: /^\d+(\.\d{1,2})?$/,
      message: 'Please enter a valid amount (e.g., 100.50)'
    },
    validate: (value) => {
      if (!value) return true; // Allow empty for optional fields
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        return 'Amount must be a positive number';
      }
      return true;
    }
  },

  // Date validation
  date: {
    required: 'Date is required',
    validate: (value) => {
      if (!value) return 'Date is required';
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return 'Please enter a valid date';
      }
      return true;
    }
  },

  // Year validation
  year: {
    required: 'Year is required',
    pattern: {
      value: /^\d{4}$/,
      message: 'Please enter a valid 4-digit year'
    },
    validate: (value) => {
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear + 1) {
        return `Year must be between 1900 and ${currentYear + 1}`;
      }
      return true;
    }
  },

  // ZIP code validation
  zipCode: {
    pattern: {
      value: /^\d{5}(-\d{4})?$/,
      message: 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)'
    }
  },

  // State code validation
  state: {
    pattern: {
      value: /^[A-Z]{2}$/,
      message: 'Please enter a valid 2-letter state code (e.g., CA, NY)'
    }
  },

  // URL validation
  url: {
    pattern: {
      value: /^https?:\/\/.+/,
      message: 'Please enter a valid URL starting with http:// or https://'
    }
  },

  // Text length validation
  maxLength: (max, fieldName = 'This field') => ({
    maxLength: {
      value: max,
      message: `${fieldName} must be no more than ${max} characters`
    }
  }),

  minLength: (min, fieldName = 'This field') => ({
    minLength: {
      value: min,
      message: `${fieldName} must be at least ${min} characters`
    }
  }),

  // Percentage validation (0-100)
  percentage: {
    pattern: {
      value: /^\d+(\.\d{1,2})?$/,
      message: 'Please enter a valid percentage'
    },
    validate: (value) => {
      if (!value) return true;
      const num = parseFloat(value);
      if (isNaN(num) || num < 0 || num > 100) {
        return 'Percentage must be between 0 and 100';
      }
      return true;
    }
  },

  // Credit score validation (300-850)
  creditScore: {
    pattern: {
      value: /^\d{3}$/,
      message: 'Credit score must be a 3-digit number'
    },
    validate: (value) => {
      if (!value) return true;
      const score = parseInt(value);
      if (isNaN(score) || score < 300 || score > 850) {
        return 'Credit score must be between 300 and 850';
      }
      return true;
    }
  }
};

// Helper function to combine validation rules
export const combineRules = (...rules) => {
  return rules.reduce((acc, rule) => {
    if (typeof rule === 'function') {
      return { ...acc, ...rule() };
    }
    return { ...acc, ...rule };
  }, {});
};

// Custom validation messages
export const validationMessages = {
  required: (fieldName) => `${fieldName} is required`,
  invalidEmail: 'Please enter a valid email address',
  invalidPhone: 'Please enter a valid phone number',
  invalidNumber: 'Please enter a valid number',
  invalidDate: 'Please enter a valid date',
  invalidUrl: 'Please enter a valid URL',
  tooShort: (fieldName, min) => `${fieldName} must be at least ${min} characters`,
  tooLong: (fieldName, max) => `${fieldName} must be no more than ${max} characters`,
  passwordMismatch: 'Passwords do not match',
  passwordWeak: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
  invalidZipCode: 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)',
  invalidState: 'Please enter a valid 2-letter state code (e.g., CA, NY)',
  invalidYear: 'Please enter a valid 4-digit year',
  invalidPercentage: 'Percentage must be between 0 and 100',
  invalidCreditScore: 'Credit score must be between 300 and 850',
  positiveNumber: 'Must be a positive number',
  invalidCurrency: 'Please enter a valid amount (e.g., 100.50)'
};

